import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Eye, EyeOff, Loader2, KeyRound, ShieldCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

// Keep in sync with backend auth.py:validate_password_policy.
const POLICY_HINT = 'At least 8 characters, with at least one number.';
const policyOk = (pw) => typeof pw === 'string' && pw.length >= 8 && /\d/.test(pw);

export default function ChangePasswordPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const forced = Boolean(user?.force_password_change);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const validationError = (() => {
    if (!currentPassword || !newPassword || !confirmPassword) return null; // not yet
    if (!policyOk(newPassword)) return POLICY_HINT;
    if (newPassword !== confirmPassword) return 'New password and confirmation do not match.';
    if (newPassword === currentPassword) return 'New password must be different from the current password.';
    return null;
  })();

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setErrorMsg('Please fill in all three fields.');
      return;
    }
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }
    setSubmitting(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      // Clear the force flag locally so the redirect doesn't fire again.
      if (forced && user) setUser({ ...user, force_password_change: false });
      toast.success('Password updated successfully');
      const redirectTo = location.state?.from?.pathname || '/';
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to update password';
      setErrorMsg(typeof detail === 'string' ? detail : 'Failed to update password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto py-10 px-4">
      {forced && (
        <Alert className="mb-4 border-amber-200 bg-amber-50 text-amber-900">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            An administrator reset your password. You must set a new password before continuing.
          </AlertDescription>
        </Alert>
      )}

      <Card className="card-dashboard" data-testid="change-password-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <KeyRound className="h-5 w-5 text-indigo-600" />
            Change Password
          </CardTitle>
          <CardDescription>
            {forced ? 'Choose a new password to access the system.' : 'Update the password you use to sign in.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <div className="space-y-1.5">
              <Label htmlFor="current">Current password</Label>
              <div className="relative">
                <Input
                  id="current"
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  data-testid="cp-current-password"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowCurrent((v) => !v)}
                  aria-label="toggle current password visibility"
                  tabIndex={-1}
                >
                  {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="newpw">New password</Label>
              <div className="relative">
                <Input
                  id="newpw"
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  data-testid="cp-new-password"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowNew((v) => !v)}
                  aria-label="toggle new password visibility"
                  tabIndex={-1}
                >
                  {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className={`text-xs flex items-center gap-1 ${newPassword && !policyOk(newPassword) ? 'text-rose-600' : 'text-slate-500'}`}>
                <ShieldCheck className="h-3 w-3" /> {POLICY_HINT}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirmpw">Confirm new password</Label>
              <Input
                id="confirmpw"
                type={showNew ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
                data-testid="cp-confirm-password"
              />
            </div>

            {(errorMsg || validationError) && (
              <Alert variant="destructive" data-testid="cp-error">
                <AlertDescription>{errorMsg || validationError}</AlertDescription>
              </Alert>
            )}

            <div className="flex gap-2 justify-end pt-2">
              {!forced && (
                <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="cp-cancel">
                  Cancel
                </Button>
              )}
              <Button type="submit" disabled={submitting} data-testid="cp-submit">
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
                Update password
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
